import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress,Tabs, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { useAuthContext } from 'common/contexts/AuthContext';
import { fetchApplicationSuite } from 'common/services/api/ApplicationSuites/ApplicationSuites.thunks';
import { isDisabledRoute } from 'common/utils/DisabledRouteEvaluator';
import { replaceLocation } from 'common/utils/locationHelpers';

export function ApplicationSuiteDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Tenants);
    const [processing, setProcessing] = useState<boolean>(true);
    const location = useLocation();
    const params = useParams();
    const { currentUserRoles } = useAuthContext();

    useEffect(() => {
        const replacedLastSegment = replaceLocation(
            lastPathSegment => lastPathSegment === params?.suiteId,
            `${location.pathname}/${Routes.Tenants}`);
        setCurrentTab(replacedLastSegment as Routes);
    }, [location]);

    const dispatch = useAppDispatch();
    const appSuite = useAppSelector(selectPageInfo);
    const [error, setError] = useState<string>();

    useEffect(() => {
        dispatch(fetchApplicationSuite(params?.suiteId ?? ''))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo({ ...result, tenantRequiresLicense: false }));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    const getTabLink = (key: string) => (
        `/${Routes.ApplicationSuites}/${params.suiteId}/${key}`
    );

    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    if (error?.includes('404')) {
        return <NotFound />;
    }

    if (error && !error?.includes('200')) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    return (
        <Page
            title={RoutesNameMap.get(Routes.ApplicationSuite)}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {appSuite?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceName: appSuite?.name,
                                resourceId: appSuite.id
                            }]}
                        />}
                    {appSuite?.id &&
                        <ResourceDetailHeader
                            resourceType='Application Suite'
                            resourceName={appSuite?.name}
                            resourceId={appSuite?.id}
                        />}
                </Typography>
            )}
        >
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem' }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={isDisabledRoute(Routes.Tenants, currentUserRoles)}
                    label={RoutesNameMap.get(Routes.Tenants)}
                    key={Routes.Tenants}
                    link={getTabLink(Routes.Tenants)}
                    value={Routes.Tenants}
                />
                <StyledTab
                    disabled={isDisabledRoute(Routes.ApiKeys, currentUserRoles)}
                    label={RoutesNameMap.get(Routes.ApiKeys)}
                    key={Routes.ApiKeys}
                    link={getTabLink(Routes.ApiKeys)}
                    value={Routes.ApiKeys}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
